import React, { Component, cloneElement } from 'react';
import { arrayOf, shape, any, element, string, bool, func, number } from 'prop-types';
import { get, isEqual, orderBy, cloneDeep, isString } from 'lodash';
import { IconButton, FormControl, Select, MenuItem } from '@material-ui/core';
import { KeyboardArrowLeft, KeyboardArrowRight} from '@material-ui/icons';
import classNames from 'classnames';
import styles from './styles.scss';

export const ASC = 'asc';
export const DESC = 'desc';
const START_PAGE = 0;
export const PAGE_SIZE = 5;
export const PAGE_SIZE_LARGE = 10;

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

export class BaseDataTable extends Component {
  static propTypes = {
    tableStyle: shape({any}),
    paginationStyle: shape({any}),
    data: arrayOf(shape({any})).isRequired,
    tableHead: element,
    tableRow: element.isRequired,
    tableSummary: element,
    pagination: bool,
    onBodyCellClick: func,
    onTableSort: func,
    setReportTableSortSelector: func,
    tableId: number,
    tableIndex: number,
    onSummaryCellClick: func,
    rowsPerPageText: string,
    initialSortStatus: shape({
      sortKey: string,
      sortType: string
    }),
    //TODO: revisit those when new report is ready
    inpdf: bool,
    rowsPerPage: number,
    isGrandTotalDisplay: bool,
    pageSize: number,
  }

  static defaultProps = {
    rowsPerPageText: 'Rows per page:',
    inpdf: false
  }

  constructor(props) {
    super(props);

    const pageSize = props.pageSize;
    this.state = {
      keys: [],
      prevData: [],
      sortedData: [],
      pageSizeOptions: [],
      currentPageIndex: START_PAGE,
      rowsPerPage: pageSize ? pageSize : PAGE_SIZE,
    }
  }

  static getDerivedStateFromProps(props, prevState) {
    const newState = {...prevState};
    if (!newState.sortStatus) {
      newState.sortStatus = {};
    }
    const prevKeys = prevState.keys;
    const newKeys = Object.keys(get(props, 'data[0]', {}));
    const { initialSortStatus, tableId } = props;
    if (!isEqual(prevKeys, newKeys)) {
      const sortStatus = newKeys.reduce((status, key) => {
        status[key] = '';
        return status;
      }, {});
      newState.keys = newKeys;
      newState.sortStatus = sortStatus;
    }

    const isDataChanged = !isEqual(props.data, prevState.prevData);
    const prevSortStatus = {
      sortKey: prevState.sortKey,
      sortType: get(prevState.sortStatus, [prevState.sortKey]),
    }
    const isTableSortSelectorChanged = tableId && initialSortStatus && !isEqual(initialSortStatus, prevSortStatus);
    if (isDataChanged || isTableSortSelectorChanged) {
      const cloneData = cloneDeep(props.data);
      let sortedData = cloneData;
      if (initialSortStatus) {
        const { sortKey, sortType } = initialSortStatus;
        newState.sortKey = cloneData.length ? sortKey : '';
        newState.sortStatus[sortKey] = sortType;
        sortedData = sortDataForTable(sortKey, sortType, cloneData);
      }
      newState.sortedData = sortedData;
      newState.prevData = cloneData;
      if (isDataChanged) {
        newState.currentPageIndex = START_PAGE;
      }
    }

    const dataLength = props.data.length;
    if(dataLength !== prevState.prevData.length){
      newState.pageSizeOptions = [];
      for (let optionIndex = 0, maxOption = PAGE_SIZE_OPTIONS.length; optionIndex < maxOption; optionIndex++) {
        const size = PAGE_SIZE_OPTIONS[optionIndex];
        newState.pageSizeOptions.push(size);
        if (size > dataLength) {
          break;
        }
      }
    }

    return newState;
  }

  componentDidUpdate(prevProps) {
    if(prevProps.pageSize !== this.props.pageSize) {
      this.setState({
        rowsPerPage: this.props.pageSize,
        currentPageIndex: 0
      });
    }
  }

  toPrevPage = () => {
    const { currentPageIndex } = this.state;
    if (currentPageIndex > 0 ) {
      this.setState({
        currentPageIndex: currentPageIndex - 1
      });
    }
  }

  toNextPage = () => {
    const { currentPageIndex, rowsPerPage, sortedData } = this.state;
    const dataLength = sortedData.length;
    if ( (currentPageIndex + 1) * rowsPerPage < dataLength ) {
      this.setState({
        currentPageIndex: currentPageIndex + 1
      });
    }
  }

  handleChangePageSize = (e) => {
    const newPageSize = e.target.value;
    this.setState({
      rowsPerPage: newPageSize,
      currentPageIndex: 0
    });
  }

  sortDataBy = (key) => () => {
    const { sortStatus, sortedData } = this.state;
    const {
      onTableSort,
      setReportTableSortSelector,
      tableId,
      tableIndex,
    } = this.props;
    const sortBy = sortStatus[key] === '' || sortStatus[key] === DESC? ASC : DESC;
    const clonedData = cloneDeep(sortedData);
    const newSortedData = sortDataForTable(key, sortBy, clonedData);

    onTableSort && onTableSort({sortKey: key, sortStatus: sortBy })
    this.setState({
      sortStatus: {
        ...sortStatus,
        [key]: sortBy
      },
      sortKey: key,
      sortedData: newSortedData
    });
    setReportTableSortSelector &&
    setReportTableSortSelector(tableId, tableIndex, {
      sortKey: key,
      sortType: sortBy,
    });
  }

  handleCellClick = (value) => (event) => {
    const { onBodyCellClick } = this.props;
    onBodyCellClick && onBodyCellClick(value, event);
  }

  handleSummaryCellClick = (value) => (event) => {
    const { onSummaryCellClick } = this.props;
    onSummaryCellClick && onSummaryCellClick(value, event);
  }

  drawTableHeader() {
    const { tableHead } = this.props;
    const { sortKey, sortStatus } = this.state;
    return cloneElement(tableHead, { onCellClick: this.sortDataBy, sortStatus, sortKey});
  }
    //TODO: revisit those when new report is ready
  getRowsPerPage() {
    const { inpdf, rowsPerPage: propRowsPerPage, pageSize } = this.props;
    const { rowsPerPage } = this.state;
    const formatPageSize = pageSize ? pageSize : PAGE_SIZE;
    return inpdf ? (propRowsPerPage || formatPageSize) : rowsPerPage;
  }

  drawTableBody() {
    const { tableRow } = this.props;
    const { sortedData, currentPageIndex } = this.state;
    const rowsPerPage = this.getRowsPerPage();
    const startItemIndex = currentPageIndex * rowsPerPage;
    const clonedData = cloneDeep(sortedData);
    const displayData = clonedData.splice(startItemIndex, rowsPerPage);
    return displayData.map((rowData, index) => {
      const key = index + JSON.stringify(rowData);
      return cloneElement(tableRow, {data:rowData, onCellClick: this.handleCellClick, key});
    });
  }

  drawTableSummary() {
    const { tableSummary } = this.props;
    return cloneElement(tableSummary, { onCellClick: this.onSummaryCellClick });
  }

  drawPagination() {
    const { rowsPerPage, currentPageIndex, sortedData, pageSizeOptions } = this.state;
    const { paginationStyle, rowsPerPageText } = this.props;
    const dataLength = sortedData.length;
    const startNum = currentPageIndex * rowsPerPage + 1;
    const pageEndNum = startNum + rowsPerPage - 1;
    const endNum = pageEndNum > dataLength? dataLength : pageEndNum;
    const disablePagination = pageSizeOptions.length < 2;
    const disablePagedown = currentPageIndex === 0;
    const disablePageUp = currentPageIndex === Math.floor(dataLength/rowsPerPage);
    return (
      <div className={classNames(styles.paginationContainer, {[paginationStyle]:paginationStyle})}>
        <div className={styles.rowsPerPageText} data-test="rowPerPageText">{rowsPerPageText}</div>
        <FormControl
          className={styles.selectForm}
          disabled={disablePagination}
        >
          <Select
            value={this.state.rowsPerPage}
            onChange={this.handleChangePageSize}
            className={styles.selectPage}
            data-test="pageSizeSelect"
          >
          {pageSizeOptions.map( pageSize => {
            return <MenuItem value={pageSize} key={pageSize} data-test={"pageSizeOption_" + pageSize}>{pageSize}</MenuItem>
          })}
          </Select>
        </FormControl>
        <div className={styles.pageSummary}>
          <span data-test="pageSummary">{`${startNum}-${endNum} of ${dataLength}`}</span>
        </div>
        <div>
          <IconButton 
            onClick={this.toPrevPage}
            disabled={disablePagedown}
            data-test="prevBtn"
          >
            <KeyboardArrowLeft/>
          </IconButton>
        </div>
        <div>
          <IconButton 
            onClick={this.toNextPage}
            disabled={disablePageUp}
            data-test="nextBtn"
          >
            <KeyboardArrowRight/>
          </IconButton>
        </div>
      </div>
    )
  }

  render() {
    //TODO: revisit those when new report is ready
    const { tableStyle, tableHead, tableRow, tableSummary, pagination, inpdf, isGrandTotalDisplay } = this.props;
    return (
      <div className={classNames(styles.tableContainer, {[tableStyle]:tableStyle})} >
        <div className={styles.tableHead}>
          {tableHead && this.drawTableHeader()}
        </div>
        <div>
          {tableRow && this.drawTableBody()}
        </div>
        <div className={styles.tableSummary}>
          {tableSummary && isGrandTotalDisplay && this.drawTableSummary()}
        </div>
        {!inpdf && <div className={styles.tablePagination}>
          {pagination && this.drawPagination()}
        </div>
        }
      </div>
    )
  }
}

import { connect } from 'react-redux';
import { setReportTableSortSelector } from '../../state/modules/app/reportBuilder/actions';
import { sortDataForTable } from '../../helpers/common';

export default connect(null, {
  setReportTableSortSelector,
})(BaseDataTable);